/* Branded "Add Lumetier to home screen" install prompt.

   Three platforms, three flows:
     - Android Chrome (and other browsers that fire `beforeinstallprompt`):
       we catch the event and trigger the native install dialog.
     - iOS Safari: no API exists — we show a styled bottom-sheet with
       step-by-step "Share → Add to Home Screen" instructions.
     - Already installed (display-mode: standalone): we render nothing.

   Dismissal is sticky for 14 days via localStorage so we don't pester
   photographers who chose not to install. */

import { useEffect, useState } from "react";
import { Sparkles, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "lumetier_install_dismissed_until";
const DISMISS_DAYS = 14;

function isInstalled() {
  if (typeof window === "undefined") return false;
  // Standalone display-mode = launched from home screen (Android + Desktop)
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari uses navigator.standalone (non-standard)
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
}

function isDismissed() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return until && Date.now() < until;
  } catch (_) { return false; }
}

function dismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400 * 1000));
  } catch (_) {}
}

export default function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);   // android-chrome install prompt
  const [iosOpen, setIosOpen] = useState(false);              // ios how-to sheet visible?
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isInstalled() || isDismissed()) return;

    // Android / Chrome / Edge — the browser tells us we're eligible.
    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS — fire our own prompt after a short delay so the page can settle.
    if (isIos()) {
      const t = setTimeout(() => setShow(true), 3500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function handleInstallClick() {
    if (deferredEvent) {
      deferredEvent.prompt();
      deferredEvent.userChoice.finally(() => {
        setDeferredEvent(null);
        setShow(false);
        // Whether they installed or not, snooze for the dismiss window.
        dismiss();
      });
    } else if (isIos()) {
      setIosOpen(true);
    }
  }

  function handleDismiss() {
    dismiss();
    setShow(false);
    setIosOpen(false);
  }

  if (!show && !iosOpen) return null;
  if (isInstalled()) return null;

  return (
    <>
      {/* Compact banner — visible while user hasn't installed and hasn't dismissed */}
      {show && !iosOpen && (
        <div
          className="lg:hidden fixed bottom-16 inset-x-3 z-50 bg-gradient-to-br from-[#1a1916] to-[#0a0908] border border-[#C9A961]/30 shadow-2xl rounded-2xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom duration-500"
          data-testid="install-prompt-banner"
          style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A961]/30 to-[#C9A961]/5 border border-[#C9A961]/40 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-[#C9A961]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] font-medium mb-0.5">
              Install
            </div>
            <div
              className="font-serif text-base text-stone-100 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Add Lumetier to your home screen
            </div>
            <p className="text-[11px] text-stone-400 mt-1 leading-relaxed">
              Launches like a real app. Push notifications when bookings come in.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={handleInstallClick}
                data-testid="install-prompt-cta"
                className="inline-flex items-center justify-center gap-1.5 px-3 h-8 bg-[#C9A961] text-stone-900 text-[10px] uppercase tracking-[0.24em] font-medium hover:opacity-90 transition-opacity rounded"
              >
                Install
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                data-testid="install-prompt-dismiss"
                className="inline-flex items-center justify-center px-3 h-8 border border-white/15 hover:border-white/30 text-stone-400 hover:text-stone-200 text-[10px] uppercase tracking-[0.24em] transition-colors rounded"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close install prompt"
            className="text-stone-500 hover:text-stone-200 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* iOS step-by-step sheet — Safari has no install API so we explain
          the 3 taps photographers need to make */}
      {iosOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-md flex items-end" data-testid="install-ios-sheet">
          <div className="w-full bg-gradient-to-b from-[#0f0e0c] to-[#0a0908] border-t border-[#C9A961]/30 rounded-t-3xl p-6 pb-10 animate-in slide-in-from-bottom duration-300">
            <div className="w-10 h-1 bg-stone-700 rounded-full mx-auto mb-6" />
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#C9A961]/30 to-[#C9A961]/5 border border-[#C9A961]/40 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#C9A961]" />
              </div>
              <h3
                className="text-2xl font-light text-stone-100 mb-2"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                Install on iPhone
              </h3>
              <p className="text-xs text-stone-500 leading-relaxed max-w-xs mx-auto">
                Three taps in Safari to add Lumetier to your home screen.
              </p>
            </div>

            <ol className="space-y-3 mb-7">
              <li className="flex items-start gap-3 text-sm text-stone-200">
                <span className="w-7 h-7 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex items-center justify-center text-[10px] font-medium text-[#C9A961] shrink-0">1</span>
                <span className="flex-1 leading-relaxed">
                  Tap the <Share className="inline w-4 h-4 mx-1 align-text-bottom text-[#C9A961]" /> <strong className="text-stone-100">Share</strong> button in the Safari toolbar
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-stone-200">
                <span className="w-7 h-7 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex items-center justify-center text-[10px] font-medium text-[#C9A961] shrink-0">2</span>
                <span className="flex-1 leading-relaxed">
                  Scroll down and tap <Plus className="inline w-4 h-4 mx-1 align-text-bottom text-[#C9A961]" /> <strong className="text-stone-100">Add to Home Screen</strong>
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-stone-200">
                <span className="w-7 h-7 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex items-center justify-center text-[10px] font-medium text-[#C9A961] shrink-0">3</span>
                <span className="flex-1 leading-relaxed">
                  Tap <strong className="text-stone-100">Add</strong> — Lumetier will appear on your home screen
                </span>
              </li>
            </ol>

            <button
              type="button"
              onClick={handleDismiss}
              data-testid="install-ios-dismiss"
              className="w-full h-12 border border-white/15 hover:border-white/30 text-stone-300 hover:text-stone-100 text-[10px] uppercase tracking-[0.28em] transition-colors rounded"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
